/**
 * Create a Webgl Feature Scene handling the background and rendering logic
 */

import Renderer from 'component/gl/renderer';
import { COLORS, LAYERS } from 'core/constants';
import { Scene } from 'three';
import Background from 'component/gl/background';
import { lerp } from '@resn/gozer-math';
import { isHandheld } from '@resn/gozer-env';
import RendererBatch from 'component/gl/renderer-batch';
import config from '@resn/gozer-core/config';

const FeatureSceneMixin = {
    options: {
        hasBackground: true,
        useRendererBatch: isHandheld,
    },

    props: {
        pXTransition: 0,
        pXOffset: 0,
        pX: (props) => props.pXTransition,

        translateYRange: (props) => (props.mobile ? [0, 0] : [props.scale * -300, props.scale * 300]),

        pYOffset: 0,
        pYBounds: (props) => {
            const { bounds, viewport, pYOffset } = props;
            return bounds.height * -0.5 + viewport.height * 0.5 - pYOffset * (props.mobile ? 0 : 1);
        },
        pYTranslate: (props) => lerp(props.scrollProgress, props.translateYRange[0], props.translateYRange[1]),
    },

    async onSetup() {
        if (this.options.useRendererBatch) {
            this.elRendererParent = this.options.ui.inner.parentElement;
        } else {
            this.renderer = Renderer.getInstance();
        }

        this.scene = new Scene();
        this.scene.name = this.constructor.name;

        if (this.options.hasBackground) await this.setupBackground();
    },

    async onAfterSetup() {},

    async setupBackground() {
        this.background = await new Background().setup({
            options: {
                addTo: this.scene,
            },
            props: {
                c: this.options.bgColor || COLORS.BLUE,
                size: () => this.props.bounds,
            },
        });
    },

    onAssetsPreloaderComplete() {
        this.scene.add(this.object);
        requestAnimationFrame(() => {
            Renderer.getRenderer().compile(this.scene);
            if (!this.props.active) this.scene.remove(this.object);
        });
    },

    onActive() {
        if (this.options.useRendererBatch) this.addRenderer();
        this.scene.add(this.object);
    },
    offActive() {
        if (this.options.useRendererBatch) this.clearRenderer();
        this.scene.remove(this.object);
    },

    addRenderer() {
        this.renderer = RendererBatch.getInstance().getAvailableRenderer();

        if (this.renderer) {
            this.renderer.props.active = true;
            this.elRendererParent.appendChild(this.renderer.renderer.domElement);
            this.setBounds(this.props.bounds);
        }
    },

    setBounds() {
        if (this.renderer && !this.renderer.options.useViewportSize) {
            this.renderer.props.width = this.props.bounds.width;
            this.renderer.props.height = this.props.bounds.height;
        }
    },

    clearRenderer() {
        if (this.renderer) {
            const domEl = this.renderer.renderer.domElement;
            if (this.elRendererParent.contains(domEl)) this.elRendererParent.removeChild(domEl);
            this.renderer.props.active = false;
            this.renderer.clear();
        }
    },

    onBeforeDestroy() {
        this.scene.traverse((child) => {
            if (child.type == 'Mesh') child.geometry.dispose();
        });
        if (this.options.useRendererBatch) this.clearRenderer();
        this.renderer = null;
    },

    onDestroy() {},

    update() {
        if (this.background && !isHandheld) this.background.y = this.props.pYBounds;
    },

    render() {
        if (!this.renderer) return;
        const { renderer, orthographicCamera: camera } = this.renderer;

        camera.layers.set(LAYERS.BACKGROUND);

        if (config.useRendererBatch) renderer.clear();
        else renderer.clearDepth();

        renderer.render(this.scene, camera);
    },
};
export default FeatureSceneMixin;
